import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, UserMinus, Calendar, User, Check } from 'lucide-react';
import participantService from '../../services/participantService';
import { toast } from 'react-hot-toast';
import PageLoader from '../ui/PageLoader';

const ParticipantDeactivation = () => {
  const { studyID, siteID, participantID } = useParams();
  const navigate = useNavigate();
  const [deactivationDate, setDeactivationDate] = useState('');
  const [confirmDeactivation, setConfirmDeactivation] = useState(false);
  const [participant, setParticipant] = useState(null);
  const [displayParticipantId, setDisplayParticipantId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParticipant = async () => {
      try {
        const data = await participantService.getParticipant(participantID);
        console.log('Full participant data:', data);
        setParticipant(data);
        
        // Extract human-readable ID (try multiple possible locations in the API response)
        const humanReadableId = data?.participant_id || 
                               data?.id || 
                               data?.participant?.participant_id || 
                               '';
        
        // If we still don't have a human-readable ID, use the breadcrumb service
        if (!humanReadableId || humanReadableId === participantID) {
          // We'll fetch from the breadcrumb service in another function
          fetchHumanReadableIdFromBreadcrumbs();
        } else {
          setDisplayParticipantId(humanReadableId);
        }
      } catch (error) {
        console.error('Error fetching participant:', error);
        toast.error('Failed to fetch participant details.');
        // Still try the breadcrumb service as a fallback
        fetchHumanReadableIdFromBreadcrumbs();
      } finally {
        setLoading(false);
      }
    };
    
    const fetchHumanReadableIdFromBreadcrumbs = async () => {
      try {
        // Import dynamically to avoid circular dependencies
        const BreadcrumbService = (await import('../../services/breadcrumbService')).default;
        const breadcrumbData = await BreadcrumbService.getBreadcrumbData();
        
        if (breadcrumbData && breadcrumbData.participants && breadcrumbData.participants[participantID]) {
          setDisplayParticipantId(breadcrumbData.participants[participantID]);
        }
      } catch (err) {
        console.error('Error fetching from breadcrumb service:', err);
      }
    };
    
    fetchParticipant();
  }, [participantID]);

  const handleRedirect = () => {
    const redirectSiteID = siteID || (participant?.site?.id);
    if (redirectSiteID) {
      navigate(`/studies/${studyID}/sites/${redirectSiteID}`);
    } else {
      console.warn('Site ID not found, redirecting to study page');
      navigate(`/studies/${studyID}`);
    }
  };

  const isNotLaterThanToday = (dateStr) => {
    let selectedDate = new Date(dateStr + " 00:00:01");
    let today = new Date();
    today.setHours(23, 59, 59);
    return selectedDate <= today;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!confirmDeactivation) {
      toast.error('Please confirm that you want to deactivate this participant.');
      return;
    }
    if (!deactivationDate) {
      toast.error('Please select a deactivation date.');
      return;
    }
    if (!isNotLaterThanToday(deactivationDate)) {
      toast.error('The selected date must be today or in the past.');
      return;
    }
	
    try {
      await participantService.deactivateParticipant(participantID, deactivationDate);
      toast.success('Participant deactivated successfully');
      handleRedirect();
    } catch (error) {
      console.error('Error deactivating participant:', error);
      toast.error('Failed to deactivate participant. Please try again.');
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div className="card overflow-hidden">
          <div className="card-band">
            <h2 className="card-band-title flex items-center gap-2">
              <UserMinus className="h-5 w-5" />
              Participant Deactivation
            </h2>
          </div>
          <div className="p-6">
                <p className="mb-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                  Do NOT complete this form if the participant is to remain active in the portal and/or appear on
                  eCOA devices. Only complete this form if the participant has ceased all activities, and should be
                  deactivated from the portal and/or device.
                </p>

                <div className="mb-6 flex items-start gap-3 rounded-lg border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-500/10">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning-500 dark:text-warning-400" />
                  <p className="text-sm text-warning-700 dark:text-warning-400">
                    PLEASE CONTACT THE HELPDESK IF A PARTICIPANT HAS BEEN DEACTIVATED IN ERROR.
                  </p>
                </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="label" htmlFor="participant-id">
                  Participant ID
                </label>
                <div className="relative">
                  <input
                    id="participant-id"
                    type="text"
                    value={displayParticipantId || participantID}
                    readOnly
                    className="input pl-10 pr-4 bg-gray-50 text-gray-600 dark:bg-white/[0.03] dark:text-gray-300"
                  />
                  <User className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                </div>
              </div>

              <div className="mb-6">
                <label className="label" htmlFor="deactivation-date">
                  Date participant was no longer active
                </label>
                <div className="relative">
                  <input
                    id="deactivation-date"
                    type="date"
                    value={deactivationDate}
                    onChange={(e) => setDeactivationDate(e.target.value)}
                    required
                    className="input pl-10 pr-4"
                  />
                  <Calendar className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={16} />
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={confirmDeactivation}
                      onChange={(e) => setConfirmDeactivation(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500/60 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-800 ${confirmDeactivation ? 'border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500' : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'}`}>
                      <Check className={`h-3.5 w-3.5 text-white transition-opacity duration-150 ${confirmDeactivation ? 'opacity-100' : 'opacity-0'}`} strokeWidth={3} />
                    </div>
                  </div>
                  <span className="ml-2.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                    YES, I want to deactivate this participant.
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleRedirect}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-danger"
                >
                  <UserMinus className="h-4 w-4" />
                  Deactivate
                </button>
              </div>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ParticipantDeactivation;